import { MACgraphModel } from "./MACgraphModel";
import { ProjectRegisterGroupBy } from "./projectRegisterTableUtils";
import {
  getProjectRegisterGroupLabel,
  ProjectRegisterGroupableFields,
  toProjectRegisterNumber,
} from "./projectRegisterGroupingUtils";

type GroupableMacGraphModel = MACgraphModel & ProjectRegisterGroupableFields;

const getYBounds = (mac: number) => {
  if (mac < 0) {
    return { y1: mac, y2: 0 };
  }

  return { y1: 0, y2: mac };
};

const getReductionMeasureName = (name: string | null | undefined) => {
  const trimmedName = name?.trim();

  return trimmedName && trimmedName.length > 0
    ? trimmedName
    : "Unspecified";
};

export const getMacGraphDataByGroup = (
  items: GroupableMacGraphModel[] | null | undefined,
  groupBy: ProjectRegisterGroupBy
): MACgraphModel[] => {
  const sourceItems = items ?? [];

  if (groupBy === "Name") {
    return sourceItems.map((item) => {
      const mac = toProjectRegisterNumber(item.mac);
      const annualEmissionReduction = toProjectRegisterNumber(item.annualEmissionReduction);
      const { y1, y2 } = getYBounds(mac);

      return {
        ...item,
        mac,
        annualEmissionReduction,
        width: toProjectRegisterNumber(item.width) || annualEmissionReduction,
        height: toProjectRegisterNumber(item.height) || mac,
        x1: toProjectRegisterNumber(item.x1),
        x2: toProjectRegisterNumber(item.x2),
        y1,
        y2,
        reductionMeasureName: getReductionMeasureName(item.reductionMeasureName),
        locationName: item.locationName ?? "",
        emissionReduction: toProjectRegisterNumber(item.emissionReduction),
        averageAnnualROI: toProjectRegisterNumber(item.averageAnnualROI),
        annualNPV: toProjectRegisterNumber(item.annualNPV),
      };
    });
  }

  const groupedItems = sourceItems.reduce<Record<string, GroupableMacGraphModel>>(
    (accumulator, item) => {
      const groupLabel = getProjectRegisterGroupLabel(
        {
          measureType: item.measureType,
          measureCategory: item.measureCategory,
          name: item.reductionMeasureName,
        },
        groupBy
      );
      const existingGroup = accumulator[groupLabel];
      const mac = toProjectRegisterNumber(item.mac);
      const annualEmissionReduction = toProjectRegisterNumber(item.annualEmissionReduction);
      const capExInvestment = toProjectRegisterNumber(item.capExInvestment);

      if (!existingGroup) {
        accumulator[groupLabel] = {
          ...item,
          reductionMeasureId: item.reductionMeasureId,
          reductionMeasureName: groupLabel,
          mac,
          annualEmissionReduction,
          emissionReduction: annualEmissionReduction,
          width: annualEmissionReduction,
          height: mac,
          x1: 0,
          x2: 0,
          y1: 0,
          y2: 0,
          locationName: item.locationName ?? "",
          averageAnnualROI: toProjectRegisterNumber(item.averageAnnualROI),
          annualNPV: toProjectRegisterNumber(item.annualNPV),
          capExInvestment,
        };
        return accumulator;
      }

      const nextMac = toProjectRegisterNumber(existingGroup.mac) + mac;
      const nextAnnualEmissionReduction =
        toProjectRegisterNumber(existingGroup.annualEmissionReduction) + annualEmissionReduction;

      existingGroup.mac = nextMac;
      existingGroup.annualEmissionReduction = nextAnnualEmissionReduction;
      existingGroup.emissionReduction = nextAnnualEmissionReduction;
      existingGroup.width = nextAnnualEmissionReduction;
      existingGroup.height = nextMac;
      existingGroup.capExInvestment =
        toProjectRegisterNumber(existingGroup.capExInvestment) + capExInvestment;

      return accumulator;
    },
    {}
  );

  let runningX = 0;

  return Object.values(groupedItems)
    .sort(
      (left, right) =>
        toProjectRegisterNumber(left.mac) - toProjectRegisterNumber(right.mac)
    )
    .map((item) => {
      const width = toProjectRegisterNumber(item.annualEmissionReduction);
      const x1 = runningX;
      const x2 = x1 + width;
      const mac = toProjectRegisterNumber(item.mac);
      const { y1, y2 } = getYBounds(mac);
      runningX = x2;

      return {
        ...item,
        mac,
        annualEmissionReduction: width,
        emissionReduction: width,
        width,
        height: mac,
        x1,
        x2,
        y1,
        y2,
        reductionMeasureName: getReductionMeasureName(item.reductionMeasureName),
        locationName: item.locationName ?? "",
        averageAnnualROI: toProjectRegisterNumber(item.averageAnnualROI),
        annualNPV: toProjectRegisterNumber(item.annualNPV),
      };
    });
};